import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import { gr } from "../tokens";
import icons from "../icons";
import GrIcon from "./GrIcon";

const TOUCH = "@media (pointer: coarse), (max-width: 899.95px)";

const focusSx = { "&.Mui-focusVisible": { boxShadow: gr.shadow.focus } };

// Filters in use, as chips under a page header or card title, so a member sees the list is
// filtered. Chip body (icon + label) opens the filter drawer; the × beside it (a sibling button,
// never a button inside a button) clears that one filter. Renders nothing when no filter is in use.
export default function GrActiveFilters({ filters, onOpen, sx }) {
  if (!filters || filters.length === 0) return null;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: gr.space[2], ...sx }}>
      {filters.map((f) => (
        <Box
          key={f.key}
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            minHeight: gr.control.sm,
            maxWidth: "100%",
            borderRadius: gr.radius.pill,
            background: gr.color.brandSoft,
            color: gr.color.brandDark,
            fontFamily: gr.font.family,
            fontSize: gr.font.size.sm,
            fontWeight: gr.font.weight.medium,
            [TOUCH]: { minHeight: gr.control.md },
          }}
        >
          <ButtonBase
            disableRipple
            onClick={onOpen}
            aria-haspopup="dialog"
            sx={{
              alignSelf: "stretch",
              gap: gr.space[2],
              minWidth: 0,
              pl: gr.space[3],
              pr: f.onClear ? gr.space[1] : gr.space[3],
              borderRadius: gr.radius.pill,
              font: "inherit",
              color: "inherit",
              "& svg": { flex: "none" },
              ...focusSx,
            }}
          >
            {f.icon ? <GrIcon icon={f.icon} size="sm" /> : null}
            <Box
              component="span"
              sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
              {f.label}
            </Box>
          </ButtonBase>
          {f.onClear ? (
            <ButtonBase
              disableRipple
              aria-label="Clear filter"
              onClick={f.onClear}
              sx={{
                flex: "none",
                width: gr.space[6],
                height: gr.space[6],
                mr: gr.space[1],
                borderRadius: gr.radius.pill,
                color: "inherit",
                transition: "background-color .15s ease",
                "&:hover": { background: gr.color.surface },
                ...focusSx,
              }}
            >
              <GrIcon icon={icons.close} size="sm" />
            </ButtonBase>
          ) : null}
        </Box>
      ))}
    </Box>
  );
}

GrActiveFilters.propTypes = {
  // One chip per filter in use; onClear adds the × that clears it.
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      icon: PropTypes.object,
      onClear: PropTypes.func,
    })
  ),
  // Chip body: opens the filter drawer.
  onOpen: PropTypes.func,
  sx: PropTypes.object,
};
